import {
  Brain, Clock, Code, Compass, FileText, Film, Globe, Image, ListChecks, Search, Sparkles, SquareTerminal, Users, Wrench,
  type LucideIcon,
} from "lucide-react";

/** 工具的中文名（给普通用户看）。认不出的保留原名。 */
const toolTitles: Record<string, string> = {
  terminal: "运行命令", process: "运行命令", execute_code: "运行代码",
  web_search: "搜索网页", web_extract: "读取网页", web_fetch: "读取网页",
  browser_navigate: "打开网页", browser_click: "操作网页", browser_snapshot: "查看网页", browser_type: "操作网页",
  read_file: "读取文件", write_file: "写入文件", patch: "修改文件", search_files: "查找文件",
  image_generate: "生成图片", video_generate: "生成视频", vision_analyze: "看图", text_to_speech: "生成语音",
  delegate_task: "派出帮手", memory: "记忆", todo: "任务清单", clarify: "向你提问",
  skill_view: "查看技能", skills_list: "查看技能", skill_manage: "管理技能",
  cronjob: "定时任务", session_search: "翻看记录", send_message: "发送消息",
};

export function toolTitle(name: string): string {
  const t = toolTitles[name];
  if (t) return t;
  if (name.startsWith("browser_")) return "操作网页";
  if (name.startsWith("mcp_") || name.includes("__")) {
    const part = name.split("_").filter(Boolean)[1];
    return "使用连接：" + (part ?? name);
  }
  return name;
}

export function toolIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (n.includes("terminal") || n.includes("shell") || n.includes("exec") || n.includes("process")) return SquareTerminal;
  if (n.includes("search")) return Search;
  if (n.includes("browser")) return Compass;
  if (n.includes("web") || n.includes("fetch") || n.includes("http")) return Globe;
  if (n.includes("read") || n.includes("file") || n.includes("write") || n.includes("patch") || n.includes("edit")) return FileText;
  if (n.includes("delegate") || n.includes("agent")) return Users;
  if (n.includes("memory")) return Brain;
  if (n.includes("todo")) return ListChecks;
  if (n.includes("video")) return Film;
  if (n.includes("image") || n.includes("vision")) return Image;
  if (n.includes("cron") || n.includes("schedule")) return Clock;
  if (n.includes("skill")) return Sparkles;
  if (n.includes("code") || n.includes("python")) return Code;
  return Wrench;
}

export function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m${Math.floor(seconds % 60)}s`;
}

/** 技能的中文名 */
const skillTitles: Record<string, string> = {
  docx: "Word 文档", xlsx: "Excel 表格", pptx: "PPT 演示", powerpoint: "PPT 演示", pdf: "PDF 处理",
  "google-workspace": "Google 办公套件", notion: "Notion", airtable: "Airtable", obsidian: "Obsidian 笔记",
  "meeting-action-items": "会议待办整理", "document-to-action-items": "文档转待办", "weekly-review-planning": "周复盘与计划",
  maps: "地图与路线", "product-price-monitor": "商品价格监控", "baoyu-infographic": "信息图", "songwriting-and-ai-music": "写歌与 AI 音乐",
  humanizer: "去 AI 味改写", "architecture-diagram": "架构图", "manim-video": "数学动画视频", p5js: "创意编程动画",
  "popular-web-designs": "网页设计参考", "claude-design": "设计助手", "design-md": "设计规范", "ascii-video": "字符动画视频",
  "youtube-content": "YouTube 内容", "gif-search": "GIF 搜索", xurl: "X（推特）", "si-feed": "AI 资讯日报",
};

const skillCategories: Record<string, string> = {
  productivity: "办公效率", creative: "创意设计", media: "音视频", research: "调研资讯", "note-taking": "笔记",
  "social-media": "社交媒体", "software-development": "编程开发", devops: "运维", web: "网页", email: "邮件",
  apple: "苹果生态", "autonomous-ai-agents": "AI 智能体", mlops: "机器学习", "data-science": "数据分析",
  github: "GitHub", gaming: "游戏", "smart-home": "智能家居", finance: "财务", health: "健康", other: "其他",
};

export const SkillNames = {
  title: (name: string) => skillTitles[name] ?? name,
  category: (raw: string) => skillCategories[raw.split("/")[0] ?? raw] ?? raw,
};
